import React, { useState, useEffect, useRef } from "react";
import COMPANY from "../data/company.js";

/* ============================================================================
   INTRO SPLASH

   The logo fills the screen, holds, then accelerates off to the left while the
   site fades in underneath it.

   Why it can't flash or jump:

   1. The site is mounted and fully painted from the first frame. It sits at
      opacity 0 behind this overlay — it is never conditionally rendered, never
      remounted, and never re-fetched. Nothing "loads" at handover.
   2. Nothing starts until the logo has actually decoded and the webfonts have
      loaded. A splash that begins before its own image is ready is what causes
      the pop most intros have.
   3. Only `transform` and `opacity` animate. Both are composited on the GPU, so
      the browser never re-lays-out or repaints during the slide.
   4. The overlay background and the site fade are the same white, cross-fading
      over the same span, so there is no bright or dark frame between them.
   ========================================================================== */

// Timings (ms). Total run ≈ ENTER + HOLD + EXIT.
const ENTER = 620;   // logo fades up
const HOLD = 780;    // sits still, readable
const EXIT = 1150;   // slides left and away
const OVERLAP = 300; // how far into EXIT the site begins fading in
const MAX_WAIT = 2500; // give up waiting on assets after this and start anyway

/** Longest the intro can possibly run. App uses this as a failsafe. */
export const INTRO_MAX_MS = MAX_WAIT + ENTER + HOLD + EXIT + 400;

// Show once per browser session. Set to true to replay on every reload while
// you're working on it.
const REPLAY_ALWAYS = false;
const SEEN_KEY = "a4:intro-seen";

/** Whether the intro should run at all. Checked before the first paint. */
export function shouldPlayIntro() {
  if (typeof window === "undefined") return false;
  const reduced =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return false; // honour the OS setting — straight to the site
  if (REPLAY_ALWAYS) return true;
  try {
    return !window.sessionStorage.getItem(SEEN_KEY);
  } catch {
    return true; // private mode or storage blocked — just play it
  }
}

const markSeen = () => {
  try {
    window.sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* storage unavailable — harmless */
  }
};

/**
 * @param {{ onReveal: () => void }} props
 *   onReveal fires when the site should start fading in, part-way through the
 *   slide rather than after it, so the two movements overlap.
 */
const IntroSplash = ({ onReveal }) => {
  // "wait"  → assets loading, plain white, nothing visible yet
  // "enter" → logo fading up at full size
  // "exit"  → sliding left, site coming in behind
  // "done"  → removed from the DOM
  const [phase, setPhase] = useState("wait");
  const timers = useRef([]);
  const revealed = useRef(false);

  const after = (ms, fn) => timers.current.push(setTimeout(fn, ms));

  useEffect(() => {
    let cancelled = false;

    const reveal = () => {
      if (revealed.current) return;
      revealed.current = true;
      onReveal();
    };

    // Wait for the logo to decode and the fonts to load, so the handover lands
    // on a page that is already final. Race against MAX_WAIT so a slow network
    // can never leave someone staring at a blank white screen.
    const img = new Image();
    img.src = COMPANY.logo;
    const decoded = img.decode
      ? img.decode().catch(() => {})
      : new Promise((res) => {
          img.onload = res;
          img.onerror = res;
        });
    const fonts = document.fonts ? document.fonts.ready.catch(() => {}) : Promise.resolve();
    const ready = Promise.all([decoded, fonts]);
    const timeout = new Promise((res) => setTimeout(res, MAX_WAIT));

    Promise.race([ready, timeout]).then(() => {
      if (cancelled) return;

      // Two rAFs guarantee the browser has painted the "enter" start state
      // before the transition begins, so the fade never gets skipped.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (cancelled) return;
          setPhase("enter");

          after(ENTER + HOLD, () => {
            setPhase("exit");
            after(OVERLAP, reveal);
            after(EXIT, () => {
              markSeen();
              setPhase("done");
            });
          });
        })
      );
    });

    // Nothing behind the overlay should scroll while it is up.
    document.body.classList.add("intro-locked");

    // NOTE: this cleanup must NOT call reveal(). React StrictMode mounts, tears
    // down and remounts every effect in development; revealing here would skip
    // the intro locally while working fine in production — the worst kind of
    // bug. App.jsx holds a failsafe timer instead.
    return () => {
      cancelled = true;
      timers.current.forEach(clearTimeout);
      document.body.classList.remove("intro-locked");
    };
  }, [onReveal]);

  useEffect(() => {
    if (phase === "done") document.body.classList.remove("intro-locked");
  }, [phase]);

  if (phase === "done") return null;

  return (
    <div className="intro" data-phase={phase} aria-hidden="true">
      <div className="intro-bg" />
      <img
        className="intro-logo"
        src={COMPANY.logo}
        alt=""
        decoding="sync"
        fetchPriority="high"
        draggable="false"
      />
    </div>
  );
};

export default IntroSplash;
